/**
 * SIMS-8138 — the reconciliation guardrail (PRD DR-8).
 *
 * A disbursement's amount should equal the earned total of the visits linked to
 * it. When it does not, the drill-down says so rather than showing a set of
 * visits that quietly fails to add up to the payout above them.
 *
 * The rule lives here, not in the panel, because SIMS-8178 AC2 requires the same
 * residual "carried through the same way it appears on screen" into Sheet 2 and
 * the PDF. Two implementations of a financial-integrity check would eventually
 * disagree, and the one place a reader would notice is a reconciliation they
 * were relying on.
 *
 * COMPLIANCE (C-3, financial data integrity): the guardrail is **not waivable**.
 * There is no flag that suppresses it and no threshold below which a difference
 * is ignored — anything that is not zero to the cent is surfaced.
 */

/**
 * How a disbursement relates to its linked visits.
 *
 * Two directions of failing to reconcile, plus `none` — SIMS-9096's case, where
 * there are no linked visits at all and so nothing to reconcile against.
 */
export type ResidualKind = "excess" | "short" | "none";

export interface DisbursementResidual {
	kind: ResidualKind;
	/** The sentence the drill-down and the exports both use. */
	label: string;
	/** Always POSITIVE — the label carries the direction, not the sign. */
	amount: number;
}

/** AC1's wording: the payout covers more than the linked visits earned. */
export const EXCESS_LABEL = "Amount not tied to a visit:";
/** AC2's wording: the payout covers less than the linked visits earned. */
export const SHORT_LABEL = "Disbursed less than earned:";
/**
 * SIMS-9096: the screen's own empty-state sentence, verbatim.
 *
 * Taken from `DisbursementVisitsPanel` rather than reworded, because SIMS-8178
 * AC2 asks for the residual to be carried through "the same way it appears on
 * screen" — the same words are the strongest reading of that.
 *
 * Note it carries no colon: it is a complete sentence, not a label with an
 * amount after it. The exports put it in the Activity column and leave the
 * earned-amount cell empty, since there is no visit to have earned anything.
 */
export const NO_VISITS_LABEL = "No linked visits.";

/**
 * Money compared at the cent, because these are two independently summed
 * currency values and binary floating point does not represent most decimal
 * amounts exactly: `0.1 + 0.2` is `0.30000000000000004`, and `10.42 * 3` is
 * `31.259999999999998` rather than `31.26`. Comparing raw would make the
 * guardrail fire on reconciled payouts and bury the real cases in the noise.
 *
 * (The QA data's common shape — 31.25 and 62.50 — happens to be exact, since
 * those are dyadic rationals. That is luck, not a property to rely on: the
 * account's 10.42 and 14.50 amounts are not.)
 */
const toCents = (n: number): number => Math.round((n ?? 0) * 100);

/**
 * The residual for one disbursement, or `null` when it reconciles (AC3).
 *
 * @param disbursedAmount what was actually PAID OUT — the transaction's amount
 * @param earnedTotal     the sum of `earnedAmount` across every linked visit
 */
export const resolveDisbursementResidual = (
	disbursedAmount: number,
	earnedTotal: number,
	visitCount?: number,
): DisbursementResidual | null => {
	// SIMS-9096 — a payout with NO linked visits is not a residual.
	//
	// AC2 of SIMS-8178 applies to "a disbursement whose amount differs from its
	// LINKED VISITS' earned total". With no linked visits there is no earned
	// total to differ from, so the case falls outside it; the excess branch below
	// would still fire, because `disbursed - 0 > 0`, and label the whole payout
	// "Amount not tied to a visit" — arithmetically true and a description of the
	// wrong situation.
	//
	// The screen never reached that branch: `DisbursementVisitsPanel` renders its
	// empty state first, so the two exports were the only surfaces asking. They
	// each got the excess label, and SIMS-8178 AC2 requires the residual to be
	// carried through "the same way it appears on screen".
	//
	// Decided here rather than guarded twice in the builders, so the screen and
	// both exports keep reading ONE rule. `visitCount` is optional: the panel does
	// not pass it and does not need to, since it short-circuits earlier.
	if (visitCount === 0) {
		return { kind: "none", label: NO_VISITS_LABEL, amount: disbursedAmount };
	}

	const diff = toCents(disbursedAmount) - toCents(earnedTotal);
	if (diff === 0) return null;

	return diff > 0
		? // AC1 — the payout exceeds what the visits earned. Showing this makes
			// the drill-down sum to the disbursed amount instead of stopping short
			// of it with no explanation.
			{ kind: "excess", label: EXCESS_LABEL, amount: diff / 100 }
		: // AC2 — the participant was paid less than the linked visits earned.
			// The mirror case, and the one that matters most to a participant.
			{ kind: "short", label: SHORT_LABEL, amount: -diff / 100 };
};
